import React from 'react';
import { useBalancedEquationsStore, PART_PRESETS, PartId } from '../store/useBalancedEquationsStore';
import {
  ChevronsDown, Link2, Flame, Pause, Save, RotateCcw, AlertTriangle, Pencil,
  Droplet, ArrowRightLeft, Wind, Beaker, FlaskConical, CheckCircle2, XCircle,
} from 'lucide-react';

export function BalancedEquationsProcedure() {
  const currentPart = useBalancedEquationsStore(s => s.currentPart);
  const partStage = useBalancedEquationsStore(s => s.partStage);
  const partAnswers = useBalancedEquationsStore(s => s.partAnswers);
  const cuCO3HeatingProgress = useBalancedEquationsStore(s => s.cuCO3HeatingProgress);
  const limewaterMilkyAmount = useBalancedEquationsStore(s => s.limewaterMilkyAmount);
  const smokeFormationAmount = useBalancedEquationsStore(s => s.smokeFormationAmount);
  const pbI2FormationProgress = useBalancedEquationsStore(s => s.pbI2FormationProgress);
  const isHeating = useBalancedEquationsStore(s => s.isHeating);
  const cuco3TempC = useBalancedEquationsStore(s => s.cuco3TempC);
  const results = useBalancedEquationsStore(s => s.results);

  // animation flags
  const isAddingReactant1 = useBalancedEquationsStore(s => s.isAddingReactant1);
  const isAddingReactant2 = useBalancedEquationsStore(s => s.isAddingReactant2);
  const isBuildingApparatus = useBalancedEquationsStore(s => s.isBuildingApparatus);
  const isBringingTubesTogether = useBalancedEquationsStore(s => s.isBringingTubesTogether);
  const isPouringMix = useBalancedEquationsStore(s => s.isPouringMix);
  const isShaking = useBalancedEquationsStore(s => s.isShaking);

  const selectPart = useBalancedEquationsStore(s => s.selectPart);
  const addCuCO3 = useBalancedEquationsStore(s => s.addCuCO3);
  const buildPartAApparatus = useBalancedEquationsStore(s => s.buildPartAApparatus);
  const togglePartAHeating = useBalancedEquationsStore(s => s.togglePartAHeating);
  const addHCl = useBalancedEquationsStore(s => s.addHCl);
  const addNH3 = useBalancedEquationsStore(s => s.addNH3);
  const removeStoppersAndBringTogether = useBalancedEquationsStore(s => s.removeStoppersAndBringTogether);
  const separateTubes = useBalancedEquationsStore(s => s.separateTubes);
  const addPbNO3 = useBalancedEquationsStore(s => s.addPbNO3);
  const addKI = useBalancedEquationsStore(s => s.addKI);
  const pourKIintoPbNO3 = useBalancedEquationsStore(s => s.pourKIintoPbNO3);
  const shakeMixedTube = useBalancedEquationsStore(s => s.shakeMixedTube);
  const updateAnswerDraft = useBalancedEquationsStore(s => s.updateAnswerDraft);
  const submitAnswer = useBalancedEquationsStore(s => s.submitAnswer);
  const resetPart = useBalancedEquationsStore(s => s.resetPart);

  const stage = partStage[currentPart];
  const animating =
    isAddingReactant1 || isAddingReactant2 || isBuildingApparatus ||
    isBringingTubesTogether || isPouringMix || isShaking;
  const preset = PART_PRESETS[currentPart];
  const answer = partAnswers[currentPart];

  return (
    <div className="flex flex-col h-full pointer-events-auto">
      <div className="pb-4 mb-4 border-b border-border/50">
        <h2 className="text-xl font-bold bg-gradient-to-r from-purple-500 to-fuchsia-500 bg-clip-text text-transparent flex items-center gap-2">
          {preset.title}
        </h2>
        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
          <FlaskConical className="w-3 h-3" /> Part {currentPart} — {preset.reactantsLabel}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
        {/* Part selector tabs */}
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Select Part</h3>
          <div className="grid grid-cols-3 gap-2">
            {(['A', 'B', 'C'] as PartId[]).map(id => {
              const done = results.some(r => r.part === id);
              const active = currentPart === id;
              return (
                <button
                  key={id}
                  onClick={() => selectPart(id)}
                  className={`py-2 rounded-lg text-sm font-semibold border transition-all ${
                    active ? 'bg-fuchsia-500 text-white border-fuchsia-500' :
                    done ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' :
                    'bg-card text-foreground border-border hover:border-fuchsia-500/40'
                  }`}
                >
                  Part {id}{done ? ' ✓' : ''}
                </button>
              );
            })}
          </div>
        </div>

        {/* Safety banner */}
        {(currentPart === 'B') && (
          <div className="mb-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Concentrated HCl and concentrated NH₃ are corrosive — work in the fume chamber.</span>
          </div>
        )}
        {(currentPart === 'A') && (
          <div className="mb-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/40 text-sm text-orange-700 dark:text-orange-300 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>The test tube becomes very hot during heating. Always handle with the wooden holder.</span>
          </div>
        )}
        {(currentPart === 'C') && (
          <div className="mb-3 p-3 rounded-lg bg-rose-500/10 border border-rose-500/40 text-sm text-rose-700 dark:text-rose-300 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Lead(II) nitrate is toxic — avoid skin contact and dispose of properly as heavy-metal waste.</span>
          </div>
        )}

        {/* Live readout */}
        <div className="mb-4 p-3 rounded-lg bg-card border border-border space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Stage</span><span className="font-semibold capitalize">{stage.replace(/-/g, ' ')}</span></div>
          {currentPart === 'A' && (
            <>
              <div className="flex justify-between"><span className="text-muted-foreground">Tube temp</span><span className="font-mono">{cuco3TempC.toFixed(0)} °C</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">CuCO₃ decomp.</span><span className="font-mono text-emerald-500">{(cuCO3HeatingProgress * 100).toFixed(0)}%</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Limewater milky</span><span className="font-mono">{(limewaterMilkyAmount * 100).toFixed(0)}%</span></div>
            </>
          )}
          {currentPart === 'B' && (
            <>
              <div className="flex justify-between"><span className="text-muted-foreground">Smoke density</span><span className="font-mono text-violet-500">{(smokeFormationAmount * 100).toFixed(0)}%</span></div>
            </>
          )}
          {currentPart === 'C' && (
            <>
              <div className="flex justify-between"><span className="text-muted-foreground">Precipitate</span><span className="font-mono text-amber-500">{(pbI2FormationProgress * 100).toFixed(0)}%</span></div>
            </>
          )}
        </div>

        {/* Step buttons per part */}
        <div className="space-y-3">
          {currentPart === 'A' && (
            <>
              <button
                disabled={stage !== 'setup' || animating}
                onClick={addCuCO3}
                className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-emerald-500/20"
              >
                <ChevronsDown className="w-4 h-4" /> {isAddingReactant1 ? 'Adding CuCO₃…' : '1. Add CuCO₃ powder to test tube'}
              </button>
              <button
                disabled={stage !== 'reactant-1' || animating}
                onClick={buildPartAApparatus}
                className="w-full py-3 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-indigo-500/20"
              >
                <Link2 className="w-4 h-4" /> {isBuildingApparatus ? 'Connecting tubing…' : '2. Connect delivery tube + limewater'}
              </button>
              {(stage === 'apparatus-built' || stage === 'reacting') && (
                <button
                  disabled={animating || (stage === 'reacting' && cuCO3HeatingProgress < 0.95 && isHeating)}
                  onClick={togglePartAHeating}
                  className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isHeating
                      ? 'bg-red-500/10 text-red-500 border border-red-500/50'
                      : 'bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-500/20'
                  }`}
                >
                  {isHeating ? <><Pause className="w-4 h-4" /> 4. Stop heating</> : <><Flame className="w-4 h-4" /> 3. Light Bunsen burner</>}
                </button>
              )}
            </>
          )}

          {currentPart === 'B' && (
            <>
              <button
                disabled={stage !== 'setup' || animating}
                onClick={addHCl}
                className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-sky-500/20"
              >
                <Droplet className="w-4 h-4" /> {isAddingReactant1 ? 'Adding HCl…' : '1. Add 3–4 drops of HCl to tube #1'}
              </button>
              <button
                disabled={stage !== 'reactant-1' || animating}
                onClick={addNH3}
                className="w-full py-3 rounded-xl bg-teal-500 hover:bg-teal-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-teal-500/20"
              >
                <Droplet className="w-4 h-4" /> {isAddingReactant2 ? 'Adding NH₃…' : '2. Add 3–4 drops of NH₃ to tube #2'}
              </button>
              <button
                disabled={stage !== 'reactant-2' || animating}
                onClick={removeStoppersAndBringTogether}
                className="w-full py-3 rounded-xl bg-violet-500 hover:bg-violet-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-violet-500/20"
              >
                <ArrowRightLeft className="w-4 h-4" /> {isBringingTubesTogether ? 'Bringing tubes together…' : '3. Remove stoppers & bring tube mouths together'}
              </button>
              {stage === 'reacting' && (
                <button
                  disabled={animating || smokeFormationAmount < 0.6}
                  onClick={separateTubes}
                  className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-amber-500/20"
                >
                  <Wind className="w-4 h-4" /> 4. Separate tubes (reaction observed)
                </button>
              )}
            </>
          )}

          {currentPart === 'C' && (
            <>
              <button
                disabled={stage !== 'setup' || animating}
                onClick={addPbNO3}
                className="w-full py-3 rounded-xl bg-slate-500 hover:bg-slate-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-slate-500/20"
              >
                <Beaker className="w-4 h-4" /> {isAddingReactant1 ? 'Pouring Pb(NO₃)₂…' : '1. Pour 2 cm³ Pb(NO₃)₂ into tube #1'}
              </button>
              <button
                disabled={stage !== 'reactant-1' || animating}
                onClick={addKI}
                className="w-full py-3 rounded-xl bg-slate-500 hover:bg-slate-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-slate-500/20"
              >
                <Beaker className="w-4 h-4" /> {isAddingReactant2 ? 'Pouring KI…' : '2. Pour 2 cm³ KI into tube #2'}
              </button>
              <button
                disabled={stage !== 'reactant-2' || animating}
                onClick={pourKIintoPbNO3}
                className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-amber-500/20"
              >
                <ArrowRightLeft className="w-4 h-4" /> {isPouringMix ? 'Pouring KI into Pb(NO₃)₂…' : '3. Pour KI into Pb(NO₃)₂'}
              </button>
              {stage === 'reacting' && (
                <button
                  disabled={animating || pbI2FormationProgress < 0.95}
                  onClick={shakeMixedTube}
                  className="w-full py-3 rounded-xl bg-yellow-500 hover:bg-yellow-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-yellow-500/20"
                >
                  <ArrowRightLeft className="w-4 h-4" /> 4. Shake the mixed tube
                </button>
              )}
            </>
          )}

          {/* Answer entry — appears once the reaction is observed */}
          {(stage === 'observed' || stage === 'answered') && (
            <div className="mt-4 p-4 rounded-xl bg-fuchsia-500/10 border border-fuchsia-500/30 space-y-3">
              <h4 className="text-fuchsia-600 dark:text-fuchsia-400 text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                <Pencil className="w-3 h-3" /> Record your findings
              </h4>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1 block">Your observation</label>
                <textarea
                  rows={3}
                  value={answer.observation}
                  onChange={e => updateAnswerDraft(currentPart, 'observation', e.target.value)}
                  disabled={answer.submitted}
                  placeholder="Describe the colour changes / gas / precipitate you see…"
                  className="w-full p-2 text-sm rounded-lg bg-background border border-border focus:border-fuchsia-500/60 focus:outline-none disabled:opacity-60 resize-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1 block">Balanced chemical equation</label>
                <input
                  type="text"
                  value={answer.equation}
                  onChange={e => updateAnswerDraft(currentPart, 'equation', e.target.value)}
                  disabled={answer.submitted}
                  placeholder="e.g. CuCO3 -> CuO + CO2"
                  className="w-full p-2 text-sm font-mono rounded-lg bg-background border border-border focus:border-fuchsia-500/60 focus:outline-none disabled:opacity-60"
                />
              </div>
              {!answer.submitted ? (
                <button
                  disabled={!answer.observation.trim() || !answer.equation.trim()}
                  onClick={() => submitAnswer(currentPart)}
                  className="w-full py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-emerald-500/20"
                >
                  <Save className="w-4 h-4" /> Submit Answer
                </button>
              ) : (
                <div className="space-y-2 text-sm">
                  <div className={`flex items-start gap-2 p-2 rounded ${answer.observationCorrect ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'}`}>
                    {answer.observationCorrect ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <XCircle className="w-4 h-4 mt-0.5" />}
                    <span>Observation: {answer.observationCorrect ? 'captures the key changes' : 'missing some key changes'}</span>
                  </div>
                  <div className={`flex items-start gap-2 p-2 rounded ${answer.equationCorrect ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'}`}>
                    {answer.equationCorrect ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <XCircle className="w-4 h-4 mt-0.5" />}
                    <span>Equation: {answer.equationCorrect ? 'correct!' : `try again — canonical: ${preset.canonicalEquation}`}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            onClick={() => resetPart(currentPart)}
            className="w-full py-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/60 text-sm flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-3 h-3" /> Reset Part {currentPart}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BalancedEquationsProcedure;
